import { useMemo, useState } from "react";
import { categoryTabs, rankings, type HotItem } from "../../../mocks/homeData";
import SectionHeading from "./SectionHeading";

const trendIcon: Record<HotItem["trend"], { cls: string; label: string }> = {
  up: { cls: "text-primary-600", label: "ri-arrow-up-line" },
  down: { cls: "text-foreground-500", label: "ri-arrow-down-line" },
  flat: { cls: "text-accent-600", label: "ri-subtract-line" },
};

export default function HotRanking() {
  const [cat, setCat] = useState("all");
  const [query, setQuery] = useState("");
  const [burstOnly, setBurstOnly] = useState(false);

  const maxHeat = Math.max(...rankings.map((r) => r.heat));

  const list = useMemo(() => {
    return rankings.filter((r) => {
      const cOk = cat === "all" || r.category === cat;
      const qOk = !query || r.title.toLowerCase().includes(query.toLowerCase()) || r.tag.includes(query);
      const bOk = !burstOnly || r.burst;
      return cOk && qOk && bOk;
    });
  }, [cat, query, burstOnly]);

  const badge = (n: number) =>
    n === 1 ? "bg-primary-500" : n === 2 ? "bg-accent-500" : n === 3 ? "bg-secondary-500" : "bg-background-200 text-foreground-600";

  return (
    <section id="ranking" className="scroll-mt-16 bg-background-50 py-14 md:py-20">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <SectionHeading
          eyebrow="今日热度总榜"
          title="爆款瓜热度排行"
          highlight="实时刷新"
          desc="综合公开社交平台与搜索热点的提及频次与讨论增量，按当日热度加权排序，帮你锁定当下最受关注的爆料焦点。"
        />

        {/* control bar */}
        <div className="mt-10 flex flex-col gap-4">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex flex-wrap gap-2">
              {categoryTabs.map((t) => (
                <button
                  key={t.key}
                  onClick={() => setCat(t.key)}
                  className={`inline-flex items-center whitespace-nowrap rounded-full px-3.5 py-1.5 text-xs font-medium transition-colors cursor-pointer ${
                    cat === t.key
                      ? "bg-foreground-950 text-background-50"
                      : "bg-background-100 text-foreground-700 hover:bg-background-200"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setBurstOnly((v) => !v)}
                className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border px-3.5 py-1.5 text-xs font-medium transition-colors cursor-pointer ${
                  burstOnly
                    ? "border-primary-500 bg-primary-500 text-white"
                    : "border-background-300 text-foreground-700 hover:border-primary-400"
                }`}
              >
                <i className="ri-flashlight-fill w-3.5 h-3.5 flex items-center justify-center"></i>
                只看爆款
              </button>
              <div className="relative">
                <i className="ri-search-line pointer-events-none absolute left-3 top-1/2 w-4 h-4 -translate-y-1/2 text-foreground-500 flex items-center justify-center"></i>
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="搜索话题 / 栏目"
                  className="w-full lg:w-52 rounded-full border border-background-300 bg-background-50 py-1.5 pl-9 pr-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
                />
              </div>
            </div>
          </div>

          {/* list */}
          <div className="mt-2 flex flex-col gap-3">
            {list.length === 0 && (
              <div className="rounded-2xl border border-dashed border-background-300 bg-background-100/50 px-6 py-12 text-center text-sm text-foreground-600">
                当前筛选下暂无上榜话题，换个栏目或关键词试试。
              </div>
            )}
            {list.map((item) => {
              const barWidth = Math.round((item.heat / maxHeat) * 100);
              const tr = trendIcon[item.trend];
              return (
                <article
                  key={item.id}
                  className="group flex items-center gap-4 rounded-2xl border border-background-200 bg-background-50 p-4 transition-colors hover:border-accent-300 hover:bg-background-100/60"
                >
                  <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl font-display text-lg text-white ${badge(item.rank)}`}>
                    {String(item.rank).padStart(2, "0")}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="font-serif text-base font-bold text-foreground-950 md:text-lg">{item.title}</h3>
                      {item.burst && (
                        <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-primary-100 px-2 py-0.5 text-[11px] font-semibold text-primary-700">
                          <i className="ri-flashlight-fill w-3 h-3 flex items-center justify-center"></i>爆款
                        </span>
                      )}
                      <span className="inline-flex items-center whitespace-nowrap rounded-full bg-secondary-100 px-2 py-0.5 text-[11px] font-medium text-secondary-900">
                        {item.tag}
                      </span>
                    </div>
                    <p className="mt-1 hidden max-w-3xl text-sm leading-relaxed text-foreground-600 sm:block">{item.summary}</p>
                    <div className="mt-2 flex items-center gap-2">
                      <div className="h-1.5 w-full max-w-[260px] overflow-hidden rounded-full bg-background-200">
                        <div className="h-full rounded-full bg-gradient-to-r from-primary-400 to-primary-500" style={{ width: `${barWidth}%` }}></div>
                      </div>
                      <span className="font-display text-xs text-primary-600">{item.heat.toLocaleString()}</span>
                    </div>
                  </div>
                  <div className="hidden shrink-0 items-center gap-2 md:flex">
                    <span className={`flex h-8 w-8 items-center justify-center rounded-full bg-background-100 ${tr.cls}`}>
                      <i className={`${tr.label} w-4 h-4 flex items-center justify-center`}></i>
                    </span>
                    <span className="text-xs font-semibold text-foreground-700">{item.delta > 0 ? `+${item.delta}` : item.delta}</span>
                  </div>
                </article>
              );
            })}
          </div>

          <p className="mt-2 text-xs text-foreground-500">
            说明：热度为公开讨论信号的综合估算值，仅作趋势参考，不代表任何立场或结论。数据每分钟滚动更新。
          </p>
        </div>
      </div>
    </section>
  );
}